// src/lib/pricing/autoGenerator.ts
// Auto-generates pricing (labor categories, CLINs, ODCs) from proposal content_json

export interface GeneratedLaborCategory {
  id: string;
  title: string;
  baseRate: number;
  loadedRate: number;
  hours: number;
  headcount: number;
  scaCode?: string;
  source: 'proposal' | 'deliverables' | 'template' | 'staffing';
}

export interface GeneratedODC {
  id: string;
  name: string;
  category: string;
  amount: number;
  quantity: number;
  unitCost: number;
  recurring: boolean;
}

export interface GeneratedCLIN {
  id: string;
  clinNumber: string;
  title: string;
  description: string;
  type: 'FFP' | 'T&M' | 'CPFF' | 'CPIF' | 'IDIQ';
  period: string;
  laborMix: Array<{
    categoryId: string;
    categoryTitle: string;
    hours: number;
    baseRate: number;
    loadedRate: number;
  }>;
  odcs: Array<{ name: string; amount: number }>;
  totalLabor: number;
  totalODC: number;
  total: number;
}

export interface GeneratedPricing {
  laborCategories: GeneratedLaborCategory[];
  clins: GeneratedCLIN[];
  odcs: GeneratedODC[];
  indirectRates: {
    fringe: number;
    overhead: number;
    gAndA: number;
    profit: number;
  };
  wrapRate: number;
  summary: {
    totalLabor: number;
    totalODC: number;
    totalPrice: number;
    periodOfPerformance: string;
    contractType: string;
  };
}

// Industry-standard indirect rate defaults by NAICS prefix
const INDUSTRY_RATES: Record<string, { fringe: number; overhead: number; gAndA: number; profit: number }> = {
  '5415': { fringe: 35, overhead: 55, gAndA: 15, profit: 10 },  // IT Services
  '5412': { fringe: 32, overhead: 45, gAndA: 12, profit: 10 },  // Professional Services
  '5613': { fringe: 38, overhead: 35, gAndA: 10, profit: 8 },   // Facilities Support
  '5617': { fringe: 40, overhead: 32, gAndA: 10, profit: 8 },   // Security
  '7225': { fringe: 42, overhead: 28, gAndA: 8, profit: 6 },    // Food Services
  '4841': { fringe: 40, overhead: 30, gAndA: 8, profit: 8 },    // Transportation
  '2362': { fringe: 45, overhead: 25, gAndA: 8, profit: 8 },    // Construction
  '6216': { fringe: 38, overhead: 45, gAndA: 12, profit: 10 },  // Healthcare
  '6117': { fringe: 35, overhead: 42, gAndA: 12, profit: 10 },  // Training
  'default': { fringe: 32, overhead: 45, gAndA: 12, profit: 10 },
};

function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

function calculateWrapRate(fringe: number, overhead: number, gAndA: number, profit: number): number {
  return (1 + fringe / 100) * (1 + overhead / 100) * (1 + gAndA / 100) * (1 + profit / 100);
}

function parseRate(rateStr: string | number | undefined): number {
  if (typeof rateStr === 'number') return rateStr;
  if (!rateStr) return 0;
  const cleaned = rateStr.toString().replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Safely extract a number from a value that could be:
 * - a number (return as-is)
 * - a string (parse it)
 * - an object like { min: X, max: Y, recommended: Z } (pick recommended, then max, then min)
 * - undefined/null (return fallback)
 */
function safeNumber(value: any, fallback: number = 0): number {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') return isNaN(value) ? fallback : value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.]/g, ''));
    return isNaN(parsed) ? fallback : parsed;
  }
  if (typeof value === 'object') {
    // Handle { min, max, recommended } or { low, high, target } patterns
    const num = value.recommended ?? value.target ?? value.max ?? value.high ?? value.min ?? value.low ?? value.total ?? value.value;
    if (num !== undefined) return safeNumber(num, fallback);
    return fallback;
  }
  return fallback;
}

function getIndirectRatesForNAICS(naicsCode?: string): { fringe: number; overhead: number; gAndA: number; profit: number } {
  if (!naicsCode) return INDUSTRY_RATES['default'];
  const prefix = naicsCode.substring(0, 4);
  return INDUSTRY_RATES[prefix] || INDUSTRY_RATES['default'];
}

function detectContractType(contentJson: any, contract: any): 'FFP' | 'T&M' | 'CPFF' | 'CPIF' | 'IDIQ' {
  const text = JSON.stringify(contentJson || {}).toLowerCase();
  const contractText = JSON.stringify(contract || {}).toLowerCase();
  const combined = text + ' ' + contractText;

  if (combined.includes('firm fixed price') || combined.includes('firm-fixed-price') || combined.includes('ffp')) return 'FFP';
  if (combined.includes('time and material') || combined.includes('time-and-material') || combined.includes('t&m')) return 'T&M';
  if (combined.includes('cost plus fixed fee') || combined.includes('cpff')) return 'CPFF';
  if (combined.includes('cost plus incentive') || combined.includes('cpif')) return 'CPIF';
  if (combined.includes('indefinite delivery') || combined.includes('idiq') || combined.includes('id/iq')) return 'IDIQ';
  return 'FFP'; // Default to FFP
}

function detectPeriodOfPerformance(contentJson: any, contract: any): string {
  // Try to extract from deliverables data
  const deliverables = contentJson?.deliverablesData;
  if (deliverables?.timeline_summary?.period_of_performance) {
    return deliverables.timeline_summary.period_of_performance;
  }

  // Try from contract structure in product definition
  if (deliverables?.product_definition?.contract_structure?.period_of_performance) {
    return deliverables.product_definition.contract_structure.period_of_performance;
  }

  // Try from contract data
  if (contract?.pop_end_date && contract?.pop_start_date) {
    return `${contract.pop_start_date} to ${contract.pop_end_date}`;
  }

  // Check text content for PoP references
  const text = JSON.stringify(contentJson || {}).toLowerCase();
  const yearMatch = text.match(/(\d+)\s*(?:year|yr)\s*base/i);
  const optionMatch = text.match(/(\d+)\s*(?:year|yr)\s*option/i);

  if (yearMatch) {
    const base = yearMatch[1];
    const options = optionMatch ? optionMatch[1] : '4';
    return `${base} year base + ${options} option years`;
  }

  return '1 year base + 4 option years';
}

/**
 * Extract labor categories from all available proposal data sources
 */
function extractLaborCategories(contentJson: any, indirectRates: { fringe: number; overhead: number; gAndA: number; profit: number }): GeneratedLaborCategory[] {
  const wrapRate = calculateWrapRate(indirectRates.fringe, indirectRates.overhead, indirectRates.gAndA, indirectRates.profit);
  const categories: GeneratedLaborCategory[] = [];
  const seenTitles = new Set<string>();

  // Source 1: Deliverables labor framework (most detailed)
  const deliverables = contentJson?.deliverablesData;
  if (deliverables?.labor_summary?.by_category) {
    for (const cat of deliverables.labor_summary.by_category) {
      const title = cat.category || cat.title;
      if (!title || seenTitles.has(title.toLowerCase())) continue;
      seenTitles.add(title.toLowerCase());

      const baseRate = safeNumber(cat.hourly_rate) || safeNumber(cat.base_rate) || estimateBaseRate(title);
      categories.push({
        id: generateId(),
        title,
        baseRate,
        loadedRate: Math.round(baseRate * wrapRate * 100) / 100,
        hours: safeNumber(cat.total_hours) || safeNumber(cat.recommended_hours),
        headcount: safeNumber(cat.headcount, 1),
        scaCode: cat.sca_code || cat.sca_title,
        source: 'deliverables'
      });
    }
  }

  // Source 2: Pricing strategy labor categories
  const pricingLabor = contentJson?.pricingStrategy?.laborCategories;
  if (pricingLabor?.length) {
    for (const cat of pricingLabor) {
      const title = cat.title || cat.category || cat.name;
      if (!title || seenTitles.has(title.toLowerCase())) continue;
      seenTitles.add(title.toLowerCase());

      const baseRate = parseRate(cat.baseRate || cat.hourlyRate || cat.rate);
      categories.push({
        id: generateId(),
        title,
        baseRate: baseRate || estimateBaseRate(title),
        loadedRate: Math.round((baseRate || estimateBaseRate(title)) * wrapRate * 100) / 100,
        hours: safeNumber(cat.hours) || safeNumber(cat.estimatedHours),
        headcount: safeNumber(cat.headcount, 1) || safeNumber(cat.count, 1),
        scaCode: cat.scaCode,
        source: 'proposal'
      });
    }
  }

  // Source 3: Staffing plan
  const staffing = contentJson?.staffingPlan?.laborCategories || contentJson?.staffingPlan?.roles;
  if (staffing?.length) {
    for (const cat of staffing) {
      const title = cat.title || cat.name || cat.role;
      if (!title || seenTitles.has(title.toLowerCase())) continue;
      seenTitles.add(title.toLowerCase());

      const baseRate = parseRate(cat.rate || cat.hourlyRate || cat.baseRate);
      categories.push({
        id: generateId(),
        title,
        baseRate: baseRate || estimateBaseRate(title),
        loadedRate: Math.round((baseRate || estimateBaseRate(title)) * wrapRate * 100) / 100,
        hours: safeNumber(cat.hours),
        headcount: safeNumber(cat.headcount, 1) || safeNumber(cat.quantity, 1),
        source: 'staffing'
      });
    }
  }

  // Source 4: Product definition staffing model
  const productStaffing = deliverables?.product_definition?.staffing_model?.roles;
  if (productStaffing?.length) {
    for (const role of productStaffing) {
      const title = role.role || role.title;
      if (!title || seenTitles.has(title.toLowerCase())) continue;
      seenTitles.add(title.toLowerCase());

      const baseRate = estimateBaseRate(title);
      categories.push({
        id: generateId(),
        title,
        baseRate,
        loadedRate: Math.round(baseRate * wrapRate * 100) / 100,
        hours: 0,
        headcount: safeNumber(role.quantity, 1),
        source: 'deliverables'
      });
    }
  }

  // Source 5: Extract from technical sections text
  if (categories.length === 0) {
    const technicalText = extractTechnicalText(contentJson);
    const inferredRoles = inferRolesFromText(technicalText);
    for (const role of inferredRoles) {
      if (seenTitles.has(role.title.toLowerCase())) continue;
      seenTitles.add(role.title.toLowerCase());

      const baseRate = estimateBaseRate(role.title);
      categories.push({
        id: generateId(),
        title: role.title,
        baseRate,
        loadedRate: Math.round(baseRate * wrapRate * 100) / 100,
        hours: 0,
        headcount: 1,
        source: 'template'
      });
    }
  }

  // Estimate hours if not provided (based on 2080 hours/year * headcount)
  const annualHours = 2080;
  for (const cat of categories) {
    if (cat.hours <= 0) {
      cat.hours = Math.round(annualHours * cat.headcount * 0.85); // 85% utilization
    }
  }

  return categories;
}

/**
 * Extract deliverables and map to ODCs
 */
function extractODCs(contentJson: any): GeneratedODC[] {
  const odcs: GeneratedODC[] = [];
  const seenNames = new Set<string>();

  // Source 1: Pricing strategy ODCs
  const pricingODCs = contentJson?.pricingStrategy?.odcs || contentJson?.pricingStrategy?.otherDirectCosts;
  if (pricingODCs?.length) {
    for (const odc of pricingODCs) {
      const name = odc.name || odc.description;
      if (!name || seenNames.has(name.toLowerCase())) continue;
      seenNames.add(name.toLowerCase());

      odcs.push({
        id: generateId(),
        name,
        category: odc.category || odc.type || categorizeODC(name),
        amount: odc.amount || odc.cost || odc.estimatedCost || 0,
        quantity: odc.quantity || 1,
        unitCost: odc.unitCost || odc.amount || 0,
        recurring: odc.recurring !== false
      });
    }
  }

  // Source 2: Deliverables-implied ODCs
  const deliverables = contentJson?.deliverablesData;
  if (deliverables?.deliverables_with_labor) {
    for (const del of deliverables.deliverables_with_labor) {
      if (del.type === 'product' || del.type === 'software' || del.type === 'training') {
        const name = `${del.deliverable_name} - ODC`;
        if (seenNames.has(name.toLowerCase())) continue;
        seenNames.add(name.toLowerCase());
        // Only add if it seems to have material costs
        if (del.material_costs || del.estimated_cost) {
          odcs.push({
            id: generateId(),
            name: del.deliverable_name,
            category: del.type,
            amount: del.material_costs || del.estimated_cost || 0,
            quantity: 1,
            unitCost: del.material_costs || del.estimated_cost || 0,
            recurring: false
          });
        }
      }
    }
  }

  // Add standard ODCs if none found
  if (odcs.length === 0) {
    odcs.push(
      { id: generateId(), name: 'Travel', category: 'travel', amount: 10000, quantity: 1, unitCost: 10000, recurring: true },
      { id: generateId(), name: 'Materials & Supplies', category: 'materials', amount: 5000, quantity: 1, unitCost: 5000, recurring: true },
    );
  }

  return odcs;
}

/**
 * Auto-generate CLINs from deliverables and labor categories
 */
function generateCLINs(
  contentJson: any,
  laborCategories: GeneratedLaborCategory[],
  odcs: GeneratedODC[],
  contractType: 'FFP' | 'T&M' | 'CPFF' | 'CPIF' | 'IDIQ',
  pop: string
): GeneratedCLIN[] {
  const clins: GeneratedCLIN[] = [];

  // Source 1: Existing CLIN structure in pricing strategy
  const existingCLINs = contentJson?.pricingStrategy?.clinStructure;
  if (existingCLINs?.length) {
    for (const clin of existingCLINs) {
      clins.push({
        id: generateId(),
        clinNumber: clin.clinNumber || clin.id || String(clins.length + 1).padStart(4, '0'),
        title: clin.title || clin.name || 'Unnamed CLIN',
        description: clin.description || '',
        type: contractType,
        period: clin.period || clin.periodOfPerformance || 'base',
        laborMix: (clin.laborMix || []).map((lm: any) => ({
          categoryId: lm.categoryId || generateId(),
          categoryTitle: lm.categoryTitle || lm.title || '',
          hours: lm.hours || 0,
          baseRate: lm.baseRate || 0,
          loadedRate: lm.loadedRate || 0
        })),
        odcs: (clin.odcs || []).map((o: any) => ({ name: o.name, amount: o.amount || 0 })),
        totalLabor: clin.totalLabor || 0,
        totalODC: clin.totalODC || 0,
        total: clin.total || clin.totalPrice || 0
      });
    }
    if (clins.length > 0) return clins;
  }

  // Source 2: Generate from deliverables
  const deliverables = contentJson?.deliverablesData?.deliverables_with_labor;
  if (deliverables?.length) {
    // Group deliverables into logical CLINs
    const clinGroups = groupDeliverablesIntoCLINs(deliverables);

    for (let i = 0; i < clinGroups.length; i++) {
      const group = clinGroups[i];
      const clinNumber = String(i + 1).padStart(4, '0');

      // Distribute labor categories across this CLIN based on deliverable labor phases
      const laborMix = distributeLaborForCLIN(group, laborCategories);
      const clinODCs = i === 0
        ? odcs.map(o => ({ name: o.name, amount: o.amount }))
        : [];

      const totalLabor = laborMix.reduce((sum, lm) => sum + (lm.hours * lm.loadedRate), 0);
      const totalODC = clinODCs.reduce((sum, o) => sum + o.amount, 0);

      clins.push({
        id: generateId(),
        clinNumber,
        title: group.title,
        description: group.description,
        type: contractType,
        period: 'base',
        laborMix,
        odcs: clinODCs,
        totalLabor: Math.round(totalLabor * 100) / 100,
        totalODC: Math.round(totalODC * 100) / 100,
        total: Math.round((totalLabor + totalODC) * 100) / 100
      });
    }
  }

  // Source 3: Generate from technical sections if no deliverables
  if (clins.length === 0 && contentJson?.technicalSections?.length) {
    for (let i = 0; i < contentJson.technicalSections.length; i++) {
      const section = contentJson.technicalSections[i];
      const clinNumber = String(i + 1).padStart(4, '0');

      // Distribute labor evenly across CLINs
      const laborMix = laborCategories.map(cat => ({
        categoryId: cat.id,
        categoryTitle: cat.title,
        hours: Math.round(cat.hours / contentJson.technicalSections.length),
        baseRate: cat.baseRate,
        loadedRate: cat.loadedRate
      }));

      const clinODCs = i === 0
        ? odcs.map(o => ({ name: o.name, amount: o.amount }))
        : [];

      const totalLabor = laborMix.reduce((sum, lm) => sum + (lm.hours * lm.loadedRate), 0);
      const totalODC = clinODCs.reduce((sum, o) => sum + o.amount, 0);

      clins.push({
        id: generateId(),
        clinNumber,
        title: section.title || `CLIN ${clinNumber}`,
        description: (section.content || '').substring(0, 200),
        type: contractType,
        period: 'base',
        laborMix,
        odcs: clinODCs,
        totalLabor: Math.round(totalLabor * 100) / 100,
        totalODC: Math.round(totalODC * 100) / 100,
        total: Math.round((totalLabor + totalODC) * 100) / 100
      });
    }
  }

  // Fallback: Create a single CLIN with all labor and ODCs
  if (clins.length === 0) {
    const laborMix = laborCategories.map(cat => ({
      categoryId: cat.id,
      categoryTitle: cat.title,
      hours: cat.hours,
      baseRate: cat.baseRate,
      loadedRate: cat.loadedRate
    }));

    const clinODCs = odcs.map(o => ({ name: o.name, amount: o.amount }));
    const totalLabor = laborMix.reduce((sum, lm) => sum + (lm.hours * lm.loadedRate), 0);
    const totalODC = clinODCs.reduce((sum, o) => sum + o.amount, 0);

    clins.push({
      id: generateId(),
      clinNumber: '0001',
      title: 'Base Period Services',
      description: 'All services as described in the Statement of Work',
      type: contractType,
      period: 'base',
      laborMix,
      odcs: clinODCs,
      totalLabor: Math.round(totalLabor * 100) / 100,
      totalODC: Math.round(totalODC * 100) / 100,
      total: Math.round((totalLabor + totalODC) * 100) / 100
    });
  }

  return clins;
}

/**
 * Group deliverables into logical CLINs
 */
function groupDeliverablesIntoCLINs(deliverables: any[]): Array<{ title: string; description: string; deliverables: any[] }> {
  // Group by type/category
  const groups: Record<string, any[]> = {};

  for (const del of deliverables) {
    const category = del.type || del.category || 'service';
    if (!groups[category]) groups[category] = [];
    groups[category].push(del);
  }

  const clinGroups: Array<{ title: string; description: string; deliverables: any[] }> = [];

  // If there are 3 or fewer categories, use them as CLINs
  const categories = Object.keys(groups);
  if (categories.length <= 5) {
    for (const category of categories) {
      const dels = groups[category];
      clinGroups.push({
        title: formatCLINTitle(category, dels),
        description: dels.map(d => d.deliverable_name || d.name).join('; '),
        deliverables: dels
      });
    }
  } else {
    // Too many categories - group into broader CLINs
    clinGroups.push({
      title: 'Program Management & Support',
      description: 'Management oversight, coordination, and administrative support',
      deliverables: deliverables.filter(d =>
        (d.type || '').match(/report|document|compliance/i) ||
        (d.deliverable_name || '').match(/management|admin|report/i)
      )
    });
    clinGroups.push({
      title: 'Technical Services',
      description: 'Core technical delivery and implementation',
      deliverables: deliverables.filter(d =>
        !(d.type || '').match(/report|document|compliance/i) &&
        !(d.deliverable_name || '').match(/management|admin|report/i)
      )
    });
  }

  // Filter out empty groups
  return clinGroups.filter(g => g.deliverables.length > 0);
}

function formatCLINTitle(category: string, deliverables: any[]): string {
  const titleMap: Record<string, string> = {
    'service': 'Professional Services',
    'document': 'Documentation & Reports',
    'report': 'Reporting & Analytics',
    'software': 'Software Development & Delivery',
    'product': 'Product Delivery',
    'training': 'Training & Education',
    'other': 'Other Services',
    'explicit': 'Core Deliverables',
    'implicit': 'Supporting Services',
    'compliance': 'Compliance & Quality Assurance',
  };

  return titleMap[category.toLowerCase()] || `${category.charAt(0).toUpperCase() + category.slice(1)} Services`;
}

/**
 * Distribute labor categories for a CLIN based on deliverable labor phases
 */
function distributeLaborForCLIN(
  clinGroup: { title: string; description: string; deliverables: any[] },
  laborCategories: GeneratedLaborCategory[]
): Array<{ categoryId: string; categoryTitle: string; hours: number; baseRate: number; loadedRate: number }> {

  // Try to use labor phases from deliverables
  const phaseHours: Record<string, number> = {};

  for (const del of clinGroup.deliverables) {
    if (del.labor_phases) {
      for (const phase of del.labor_phases) {
        if (phase.labor_categories) {
          for (const lc of phase.labor_categories) {
            const catName = lc.category || lc.title;
            const hours = safeNumber(phase.hours_estimate, 40);
            const catHours = Math.round(hours * (lc.hours_percent || 25) / 100);
            phaseHours[catName] = (phaseHours[catName] || 0) + catHours;
          }
        }
      }
    }
  }

  // If we have phase hours, map them to our labor categories
  if (Object.keys(phaseHours).length > 0) {
    return laborCategories.map(cat => {
      // Find matching phase hours
      const matchKey = Object.keys(phaseHours).find(k =>
        k.toLowerCase().includes(cat.title.toLowerCase()) ||
        cat.title.toLowerCase().includes(k.toLowerCase())
      );
      const hours = matchKey ? phaseHours[matchKey] : Math.round(cat.hours / 4); // Fallback: 25% per CLIN

      return {
        categoryId: cat.id,
        categoryTitle: cat.title,
        hours,
        baseRate: cat.baseRate,
        loadedRate: cat.loadedRate
      };
    }).filter(lm => lm.hours > 0);
  }

  // Fallback: distribute evenly
  const numCLINs = Math.max(1, 3); // Assume ~3 CLINs total
  return laborCategories.map(cat => ({
    categoryId: cat.id,
    categoryTitle: cat.title,
    hours: Math.round(cat.hours / numCLINs),
    baseRate: cat.baseRate,
    loadedRate: cat.loadedRate
  }));
}

/**
 * Estimate base hourly rate from role title
 */
function estimateBaseRate(title: string): number {
  const t = title.toLowerCase();

  // Executive / Director level
  if (t.includes('director') || t.includes('vice president') || t.includes('chief')) return 105;
  if (t.includes('program manager') || t.includes('programme manager')) return 90;
  if (t.includes('project manager')) return 80;

  // Senior technical
  if (t.includes('architect') || t.includes('principal')) return 95;
  if (t.includes('senior') && (t.includes('engineer') || t.includes('developer'))) return 85;
  if (t.includes('senior') && t.includes('analyst')) return 75;
  if (t.includes('senior') && (t.includes('consultant') || t.includes('specialist'))) return 80;
  if (t.includes('lead') || t.includes('technical lead')) return 85;

  // Mid-level technical
  if (t.includes('engineer') || t.includes('developer')) return 65;
  if (t.includes('analyst')) return 55;
  if (t.includes('consultant') || t.includes('specialist')) return 60;
  if (t.includes('designer')) return 60;
  if (t.includes('cybersecurity') || t.includes('security analyst')) return 75;
  if (t.includes('systems admin') || t.includes('sysadmin')) return 55;

  // Healthcare
  if (t.includes('physician') || t.includes('doctor')) return 150;
  if (t.includes('nurse practitioner') || t.includes('np')) return 85;
  if (t.includes('registered nurse') || t.includes('rn')) return 55;
  if (t.includes('lpn') || t.includes('licensed practical')) return 35;
  if (t.includes('medical assistant')) return 22;

  // Trades / Maintenance
  if (t.includes('supervisor')) return 42;
  if (t.includes('hvac') || t.includes('electrician') || t.includes('plumber')) return 38;
  if (t.includes('mechanic') || t.includes('technician')) return 35;
  if (t.includes('maintenance worker') || t.includes('general maintenance')) return 28;

  // Transportation
  if (t.includes('cdl') && t.includes('class a')) return 32;
  if (t.includes('driver') || t.includes('cdl')) return 28;
  if (t.includes('dispatcher')) return 28;

  // Food services
  if (t.includes('chef') || t.includes('food service manager')) return 35;
  if (t.includes('cook')) return 22;
  if (t.includes('food service worker') || t.includes('cafeteria')) return 16;

  // Security
  if (t.includes('security manager')) return 42;
  if (t.includes('armed') && t.includes('security')) return 28;
  if (t.includes('security') && (t.includes('officer') || t.includes('guard'))) return 22;

  // Support
  if (t.includes('admin') || t.includes('clerical') || t.includes('coordinator')) return 28;
  if (t.includes('help desk') || t.includes('support')) return 30;
  if (t.includes('data entry') || t.includes('clerk')) return 22;
  if (t.includes('janitor') || t.includes('custod')) return 18;
  if (t.includes('laborer') || t.includes('general labor')) return 22;

  // Instructor / Trainer
  if (t.includes('instructor') || t.includes('trainer')) return 52;
  if (t.includes('curriculum')) return 55;

  // Warehouse / Logistics
  if (t.includes('logistics') || t.includes('warehouse')) return 38;
  if (t.includes('forklift')) return 25;
  if (t.includes('material handler') || t.includes('shipping')) return 22;

  // Default mid-level
  return 50;
}

function extractTechnicalText(contentJson: any): string {
  const parts: string[] = [];
  if (contentJson?.technicalSections) {
    for (const section of contentJson.technicalSections) {
      parts.push(section.content || '');
    }
  }
  if (contentJson?.coverLetter) parts.push(contentJson.coverLetter);
  if (contentJson?.sourceGroundedOutput?.proposal?.technicalApproach?.content) {
    parts.push(contentJson.sourceGroundedOutput.proposal.technicalApproach.content);
  }
  if (contentJson?.sourceGroundedOutput?.proposal?.personnel?.content) {
    parts.push(contentJson.sourceGroundedOutput.proposal.personnel.content);
  }
  return parts.join('\n');
}

function inferRolesFromText(text: string): Array<{ title: string }> {
  const rolePatterns = [
    /program\s+manager/gi, /project\s+manager/gi,
    /senior\s+(?:software\s+)?engineer/gi, /software\s+engineer/gi,
    /systems?\s+administrator/gi, /systems?\s+analyst/gi,
    /(?:senior\s+)?(?:developer|programmer)/gi,
    /(?:senior\s+)?analyst/gi, /(?:senior\s+)?consultant/gi,
    /help\s+desk\s+(?:technician|specialist)/gi,
    /technical\s+lead/gi, /team\s+lead/gi,
    /(?:senior\s+)?(?:network|database|cloud)\s+(?:engineer|administrator)/gi,
    /cybersecurity\s+(?:analyst|engineer|specialist)/gi,
    /quality\s+assurance/gi, /qa\s+(?:analyst|engineer)/gi,
    /data\s+(?:analyst|scientist|engineer)/gi,
    /business\s+analyst/gi,
    /(?:technical\s+)?(?:writer|documentation)/gi,
    /administrative\s+(?:assistant|support)/gi,
    /subject\s+matter\s+expert/gi,
  ];

  const found: Array<{ title: string }> = [];
  const seen = new Set<string>();

  for (const pattern of rolePatterns) {
    const matches = text.match(pattern);
    if (matches) {
      for (const match of matches) {
        const normalized = match.trim().replace(/\s+/g, ' ');
        const titleCase = normalized.replace(/\b\w/g, c => c.toUpperCase());
        if (!seen.has(titleCase.toLowerCase())) {
          seen.add(titleCase.toLowerCase());
          found.push({ title: titleCase });
        }
      }
    }
  }

  // If nothing found, add some defaults
  if (found.length === 0) {
    return [
      { title: 'Program Manager' },
      { title: 'Senior Analyst' },
      { title: 'Analyst' },
      { title: 'Administrative Support' }
    ];
  }

  return found;
}

function categorizeODC(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('travel')) return 'travel';
  if (n.includes('equipment') || n.includes('hardware')) return 'equipment';
  if (n.includes('software') || n.includes('license')) return 'software';
  if (n.includes('material') || n.includes('suppl')) return 'materials';
  if (n.includes('training') || n.includes('cert')) return 'training';
  if (n.includes('vehicle') || n.includes('fleet')) return 'vehicles';
  if (n.includes('fuel') || n.includes('gas')) return 'fuel';
  if (n.includes('food') || n.includes('provision')) return 'food_services';
  if (n.includes('subcontract')) return 'subcontractor';
  return 'other';
}

/**
 * Main function: Generate complete pricing from proposal data
 */
export function generatePricingFromProposal(
  contentJson: any,
  contract: any,
  companyIndirectRates?: { fringe_rate: number; overhead_rate: number; ga_rate: number; profit_rate: number } | null
): GeneratedPricing {
  // Determine indirect rates
  const naicsCode = contract?.naics_code || contentJson?.contractOverview?.naicsCode;
  const industryDefaults = getIndirectRatesForNAICS(naicsCode);

  const indirectRates = {
    fringe: companyIndirectRates?.fringe_rate || industryDefaults.fringe,
    overhead: companyIndirectRates?.overhead_rate || industryDefaults.overhead,
    gAndA: companyIndirectRates?.ga_rate || industryDefaults.gAndA,
    profit: companyIndirectRates?.profit_rate || industryDefaults.profit,
  };

  const wrapRate = calculateWrapRate(indirectRates.fringe, indirectRates.overhead, indirectRates.gAndA, indirectRates.profit);

  // Detect contract type and PoP
  const contractType = detectContractType(contentJson, contract);
  const pop = detectPeriodOfPerformance(contentJson, contract);

  // Extract / generate components
  const laborCategories = extractLaborCategories(contentJson, indirectRates);
  const odcs = extractODCs(contentJson);
  const clins = generateCLINs(contentJson, laborCategories, odcs, contractType, pop);

  // Calculate totals
  const totalLabor = clins.reduce((sum, c) => sum + c.totalLabor, 0);
  const totalODC = clins.reduce((sum, c) => sum + c.totalODC, 0);

  return {
    laborCategories,
    clins,
    odcs,
    indirectRates,
    wrapRate: Math.round(wrapRate * 100) / 100,
    summary: {
      totalLabor: Math.round(totalLabor * 100) / 100,
      totalODC: Math.round(totalODC * 100) / 100,
      totalPrice: Math.round((totalLabor + totalODC) * 100) / 100,
      periodOfPerformance: pop,
      contractType,
    }
  };
}
